/**
 * The backend reports a refusal as a GraphQL error whose `extensions.violations[0]` carries the
 * human-readable `message` and an HTTP-like `status` (fleet convention; the top-level `message` is
 * null). A non-null mutation also yields a second `NullValueInNonNullableField` error with no
 * violations at all, so the first error is not always the one to read.
 *
 * Null-safe on `unknown` (ApolloError, plain Error, or anything) and on what is inside: a `message`
 * that is not a string or a `status` that is not a number comes back as null. Never throws.
 */
export interface ReportedViolation {
  message: string | null;
  status: number | null;
}

interface ApolloErrorLike {
  graphQLErrors?: unknown;
}

interface GraphQLErrorLike {
  extensions?: { violations?: unknown } | null;
}

/** Every violation across all graphQLErrors, in order. */
export function violationsOf(err: unknown): ReportedViolation[] {
  const apolloLike = (typeof err === 'object' && err !== null ? err : {}) as ApolloErrorLike;
  const gqlErrors: unknown[] = Array.isArray(apolloLike.graphQLErrors) ? apolloLike.graphQLErrors : [];

  return gqlErrors.flatMap((gqlError) => {
    const violations = (gqlError as GraphQLErrorLike | null)?.extensions?.violations;
    if (!Array.isArray(violations)) {
      return [];
    }
    return violations
      .filter((violation): violation is Record<string, unknown> => typeof violation === 'object' && violation !== null)
      .map((violation) => ({
        message: typeof violation.message === 'string' ? violation.message : null,
        status: typeof violation.status === 'number' ? violation.status : null,
      }));
  });
}

export function firstViolation(err: unknown): ReportedViolation | null {
  return violationsOf(err)[0] ?? null;
}
